import './RichTextInput.less';
import 'draft-js/dist/Draft.css';
import React, {useState, useEffect, useRef} from 'react';
import {setVerticalSticky, unsetVerticalSticky} from "../../../bootstrap/dom/event-handlers/vertical-scroll-sticky";
import {getLayoutHeader, getLayoutScrollElement} from "../../../bootstrap/dom/element/queries";
import {
    Editor,
    EditorState,
    ContentState,
    RichUtils,
    DefaultDraftBlockRenderMap,
    CompositeDecorator,
    convertToRaw,
    convertFromRaw
} from 'draft-js';
import BlockToolbar from './components/BlockToolbar';
import InlineToolbar from './components/InlineToolbar';
import useImage from './hooks/useImage';
import useLink from './hooks/useLink';
import useIframe from './hooks/useIframe';
import useVideo from "./hooks/useVideo";
import useAudio from "./hooks/useAudio";
import imageDecoration from './decorations/imageDecoration';
import linkDecoration from './decorations/linkDecoration';
import iframeDecoration from './decorations/iframeDecoration';
import videoDecoration from "./decorations/videoDecoration";
import audioDecoration from "./decorations/audioDecoration";

const TOOLBAR_MODE_FULL = 'full';
const TOOLBAR_MODE_BASIC = 'basic';

export default function RichTextInput(
    {
        defaultValue = null,
        onChange = (value) => {},
        toolbarMode = TOOLBAR_MODE_FULL,
        trimmed = true,
    }
) {

    const decorator = new CompositeDecorator([
        imageDecoration,
        videoDecoration,
        audioDecoration,
        linkDecoration,
        iframeDecoration,
    ]);

    const getInitialContent = () => {
        try {
            return convertFromRaw(JSON.parse(defaultValue || ''));
        } catch (error) {
            return ContentState.createFromText('');
        }
    };

    const [editorState, setEditorState] = useState(() => EditorState.createWithContent(getInitialContent(), decorator));

    const [
        imageButton,
        imagePicker,
    ] = useImage(editorState, setEditorState);

    const [
        videoButton,
        videoPicker,
    ] = useVideo(editorState, setEditorState);

    const [
        audioButton,
        audioPicker,
    ] = useAudio(editorState, setEditorState);

    const [
        linkButton,
        linkDialog,
    ] = useLink(editorState, setEditorState);

    const [
        iframeButton,
        iframeDialog,
    ] = useIframe(editorState, setEditorState);

    useEffect(() => {
        const content = convertToRaw(editorState.getCurrentContent());

        if (content.blocks.every(b => (trimmed ? b.text.trim() : b.text) === '')) {
            onChange(null);
            return;
        }

        onChange(JSON.stringify(content));
    }, [editorState]);

    const handleChange = editorState => {
        setEditorState(editorState);
    };

    const toggleBlockType = (blockType) => {
        setEditorState(
            RichUtils.toggleBlockType(editorState, blockType)
        );
    };

    const toggleInlineType = (inlineType) => {
        setEditorState(
            RichUtils.toggleInlineStyle(editorState, inlineType)
        );
    };

    return <div className="rich-text-input">
        <div className="toolbars">
            <div className="toolbar-group">
                <BlockToolbar
                    editorState={editorState}
                    onToggle={toggleBlockType}
                />
                <InlineToolbar
                    editorState={editorState}
                    onToggle={toggleInlineType}
                />
                {
                    toolbarMode === TOOLBAR_MODE_FULL &&
                    <div className="toolbar">
                        {imageButton}
                        {videoButton}
                        {audioButton}
                        {iframeButton}
                        {linkButton}
                    </div>
                }
            </div>
        </div>
        <Editor
            editorState={editorState}
            onChange={handleChange}
        />
        {imagePicker}
        {videoPicker}
        {audioPicker}
        {iframeDialog}
        {linkDialog}
    </div>;
}


export {
    TOOLBAR_MODE_FULL,
    TOOLBAR_MODE_BASIC,
}
